import { createApi } from '@reduxjs/toolkit/query/react';

import { fetchBaseQueryWithTokenCheck } from '@shared/utils/api.utils';

import type {
  OpAgentDetailsFormValues,
  StandaloneAgentDetailsFormValues,
} from '../components/modals/create-agent/sub-components/general/generic-agent-details.schema';

import type { ArtifactsResponse, CreateAgentResponse } from './create-agent.types';

export type CreateOpAgentBody = OpAgentDetailsFormValues;
export type CreateStandaloneAgentBody = StandaloneAgentDetailsFormValues;

enum Endpoint {
  ARTIFACTS = 'Artifacts',
}

export const agentManagementApiSlice = createApi({
  reducerPath: 'agentManagementApi',
  baseQuery: fetchBaseQueryWithTokenCheck,
  tagTypes: [Endpoint.ARTIFACTS],
  endpoints: (builder) => {
    return {
      createOpAgentPackageLink: builder.mutation<CreateAgentResponse, CreateOpAgentBody>({
        query: (body) => ({
          url: '/api/v1/agent/op',
          method: 'POST',
          body,
        }),
      }),
      createStandaloneAgentPackageLink: builder.mutation<CreateAgentResponse, CreateStandaloneAgentBody>({
        query: (body) => ({
          url: '/api/v1/agent/standalone',
          method: 'POST',
          body,
        }),
      }),
      regenerateAgentPackageLink: builder.mutation<CreateAgentResponse, { agentId: string }>({
        query: ({ agentId }) => ({
          url: `/api/v1/agent/${agentId}/bundle`,
          method: 'POST',
        }),
      }),
      getArtifacts: builder.query<ArtifactsResponse, void>({
        query: () => ({
          url: '/api/v1/standalone/artifacts/abap',
          method: 'GET',
        }),
        providesTags: [Endpoint.ARTIFACTS],
      }),
      uploadStandaloneArtifact: builder.mutation<{ success: boolean }, { file: File }>({
        query: ({ file }) => {
          const formData = new FormData();
          formData.append('artifact', file, file.name);

          return {
            url: `/api/v1/standalone/artifacts/abap?component=jco&releaseNotes=Jco+upload`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: [Endpoint.ARTIFACTS],
      }),
    };
  },
});

export const {
  useCreateOpAgentPackageLinkMutation,
  useCreateStandaloneAgentPackageLinkMutation,
  useRegenerateAgentPackageLinkMutation,
  useGetArtifactsQuery,
  useUploadStandaloneArtifactMutation,
} = agentManagementApiSlice;
